import {Controller} from "@hotwired/stimulus"

export default class extends Controller {
    static targets = ["editor", "searchInput", "results", "pagination", "myImagesTab", "libraryTab"]

    connect() {
        this.currentView = "my_images"
        this.updateTabStyles()
        this.loadImages()
    }

    search(event) {
        if (event.type === "keyup" && event.key !== "Enter") return

        this.loadImages()
    }

    switchToMyImages() {
        this.currentView = "my_images"
        this.updateTabStyles()
        this.loadImages()
    }

    switchToLibrary() {
        this.currentView = ""  // Empty string will use the default (library) in the controller
        this.updateTabStyles()
        this.loadImages()
    }

    updateTabStyles() {
        // Remove active styles from both tabs
        this.myImagesTabTarget.classList.remove("border-primary-500", "text-primary-600", "dark:text-primary-400", "dark:border-primary-400")
        this.libraryTabTarget.classList.remove("border-primary-500", "text-primary-600", "dark:text-primary-400", "dark:border-primary-400")

        // Add inactive styles to both tabs
        this.myImagesTabTarget.classList.add("border-transparent", "text-slate-500", "dark:text-slate-400")
        this.libraryTabTarget.classList.add("border-transparent", "text-slate-500", "dark:text-slate-400")

        // Add active styles to the selected tab
        if (this.currentView === "my_images") {
            this.myImagesTabTarget.classList.remove("border-transparent", "text-slate-500", "dark:text-slate-400")
            this.myImagesTabTarget.classList.add("border-primary-500", "text-primary-600", "dark:text-primary-400", "dark:border-primary-400")
        } else {
            this.libraryTabTarget.classList.remove("border-transparent", "text-slate-500", "dark:text-slate-400")
            this.libraryTabTarget.classList.add("border-primary-500", "text-primary-600", "dark:text-primary-400", "dark:border-primary-400")
        }
    }

    loadImages(page = 1) {
        const query = this.searchInputTarget.value
        const url = `/articles/images?format=json&page=${page}&filter[title]=${encodeURIComponent(query)}${this.currentView ? `&view=${this.currentView}` : ''}`

        fetch(url)
            .then(response => response.json())
            .then(data => {
                this.renderImages(data.images)
                this.renderPagination(data.pagy)
            })
            .catch(error => console.error("Error loading images:", error))
    }

    renderImages(images) {
        const html = images.map(image => {
            return `
        <div class="bg-white dark:bg-slate-700 rounded-lg shadow p-2">
          <img src="${image.thumb_url}" alt="${image.title}" class="w-full h-auto rounded">
          <div class="mt-2">
            <h4 class="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">${image.title}</h4>
            <div class="mt-2 flex justify-between space-x-2">
              <button type="button" class="text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 px-2 py-1 rounded" 
                data-action="click->article-images#insertImage" 
                data-size="small" 
                data-url="${image.article_220_url}" 
                data-id="${image.id}">
                Small
              </button>
              <button type="button" class="text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 px-2 py-1 rounded" 
                data-action="click->article-images#insertImage" 
                data-size="medium" 
                data-url="${image.article_380_url}" 
                data-id="${image.id}">
                Medium
              </button>
              <button type="button" class="text-xs bg-slate-100 hover:bg-slate-200 dark:bg-slate-600 dark:hover:bg-slate-500 text-slate-700 dark:text-slate-200 px-2 py-1 rounded" 
                data-action="click->article-images#insertImage" 
                data-size="large" 
                data-url="${image.article_580_url}" 
                data-id="${image.id}">
                Large
              </button>
            </div>
          </div>
        </div>
      `
        }).join('')

        this.resultsTarget.querySelector('.grid').innerHTML = images.length ? html : '<p class="col-span-4 text-center text-slate-500 dark:text-slate-400">No images found</p>'
    }

    renderPagination(pagy) {
        if (!pagy || pagy.pages <= 1) {
            this.paginationTarget.innerHTML = ''
            return
        }

        let html = '<nav class="flex items-center justify-center space-x-1">'

        // Previous page
        if (pagy.prev) {
            html += `<button type="button" class="px-3 py-1 rounded-md bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600" data-action="click->article-images#changePage" data-page="${pagy.prev}">Previous</button>`
        }

        // Page numbers - limit to a reasonable number
        const currentPage = pagy.page;
        const totalPages = pagy.pages;

        // Show first page, last page, and a few pages around the current page
        const pagesToShow = [];

        // Always show page 1
        pagesToShow.push(1);

        // Show a few pages around the current page
        for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
            pagesToShow.push(i);
        }

        // Always show the last page if there are more than 1 pages
        if (totalPages > 1) {
            pagesToShow.push(totalPages);
        }

        // Sort and remove duplicates
        const uniquePages = [...new Set(pagesToShow)].sort((a, b) => a - b);

        // Render the page numbers with ellipses where needed
        let prevPage = 0;
        for (const page of uniquePages) {
            // Add ellipsis if there's a gap
            if (page - prevPage > 1) {
                html += `<span class="px-3 py-1">...</span>`;
            }

            // Add the page button
            if (page === currentPage) {
                html += `<button type="button" class="px-3 py-1 rounded-md bg-primary-600 text-white" disabled>${page}</button>`;
            } else {
                html += `<button type="button" class="px-3 py-1 rounded-md bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600" data-action="click->article-images#changePage" data-page="${page}">${page}</button>`;
            }

            prevPage = page;
        }

        // Next page
        if (pagy.next) {
            html += `<button type="button" class="px-3 py-1 rounded-md bg-white dark:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-300 dark:border-slate-600" data-action="click->article-images#changePage" data-page="${pagy.next}">Next</button>`
        }

        html += '</nav>'
        this.paginationTarget.innerHTML = html
    }

    changePage(event) {
        const page = event.currentTarget.dataset.page
        this.loadImages(page)
    }

    insertImage(event) {
        const button = event.currentTarget
        const imageUrl = button.dataset.url
        const imageId = button.dataset.id
        const size = button.dataset.size

        // Create image HTML based on size
        let imageHtml = `<img src="${imageUrl}" alt="Article image" class="img-${size}">`

        // Insert at cursor position or at the end of the editor
        const editor = this.editorTarget

        // For Trix editor (ActionText)
        // First check if the editor is a trix-editor element itself
        if (editor.tagName === 'TRIX-EDITOR' && editor.editor) {
            editor.editor.insertHTML(imageHtml)
        }
        // Then check if it contains a trix-editor element
        else if (editor.querySelector('trix-editor')) {
            const trixEditor = editor.querySelector('trix-editor')
            trixEditor.editor.insertHTML(imageHtml)
        }
        // For regular textarea
        else {
            editor.value += imageHtml
        }
    }
}
